import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import CreatableSelect from '@components/BaseComponents/CreatableSelect';

import { useForm, Controller } from 'react-hook-form';
import ErrorWrapper from '@components/BaseComponents/ErrorWrapper';
import Form from 'deltoid/core/Form';
import FormFooter from '@components/BaseComponents/FormFooter';
import { Row, Col } from 'deltoid/core/Blocks/BootstrapContainers';
import { pancardNumberRegex, cinRegex, emailRegex } from '@utils/validations';
import { masterDataSelectors, merchantSelectors, signingAuthorityActions, signingAuthoritySelectors } from '@store/ducks';

const SigningAuthority = ({
  businessEntities,
  merchantDetails,
  fetchAuthorityNames,
  getAuthorityNames,
  filledDetails,
  submitSigningAuthorityDetails,
  editable,
  why,
}) => {
  const [contactId, setContactId] = useState(false);
  const [validations, setValidations] = useState({
    cin: { show: false },
    pan: { default: '', readOnly: false },
    email: { default: '', readOnly: false },
    name: { default: '', readOnly: false },
  });
  const { register, handleSubmit, errors, reset, control, setValue } = useForm(
    { mode: 'onChange' },
  );

  useEffect(() => {
    const { business_entity_id, pancard_number, email, pancard_name } = merchantDetails || {};
    const { list } = businessEntities || { list: [] };
    const { name: entityType } =  list.find(
      (entity) => business_entity_id === entity.id
    ) || { name: null };
    let formValidations = {};
    switch(entityType) {
      case 'Sole Proprietorship':
      case 'Individual':
        formValidations = {
          cin: { show: false },
          pan: { default: pancard_number, readOnly: true },
          email: { default: email },
          name: { default: pancard_name, readOnly: true },
        };
        break;
      case 'Public Limited':
      case 'Private Limited':
      case 'LLP':
      case 'One Person Company':
        formValidations = {
          cin: { show: true, required: true },
          pan: {},
          email: { default: email },
          name: {},
        };
        break;
      case 'Partnership':
        formValidations = {
          cin: { show: false },
          pan: {},
          email: { default: email },
          name: { textField: true },
        };
        break;
      case 'NGO':
      case 'Government':
      case 'Trust':
        formValidations = {
          cin: { show: false },
          pan: {},
          email: { default: email },
          name: { textField: true },
        };
        break;
      default:
        formValidations = validations;
    };
    if (filledDetails && filledDetails.length) {
      const { cin_number: cin, pancard_number: pan, email, name, id } = filledDetails[0];
      setContactId(id);
      formValidations.cin.filled = cin;
      formValidations.pan.filled = pan;
      formValidations.email.filled = email;
      formValidations.name.filled = formValidations.name.readOnly || formValidations.name.textField ? name : {
        label: name, value: name,
      };
    }
    setValidations(formValidations);
    if (formValidations.cin.filled)
      fetchAuthorityNames(formValidations.cin.filled);
    reset({
      signingAuthorityName: formValidations.name.filled || formValidations.name.default,
      signingPanNumber: formValidations.pan.filled || formValidations.pan.default,
      signatoryEmail: formValidations.email.filled || formValidations.email.default,
      cin: formValidations.cin.filled || null,
    })
  }, [merchantDetails, businessEntities, filledDetails]);

  const handleCINOnChange = (cin) => {
    if (!cinRegex(cin)) fetchAuthorityNames(cin);
  }

  const validateAndSetName = (name) => {
    setValue('signingAuthorityName', { label: name, value: name }, { shouldValidate: true })
  };

  const onSubmit = (formData) => {
    if (editable)
      submitSigningAuthorityDetails({ ...formData, contactId })
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} data-testid='signing-authority-form'>
      <Row className='p-4'>
        {
          validations.cin.show && (
            <Col md={6}>
              <Form.Group>
                <Form.Label id="cin">
                  CIN / LLPIN (Company / LLP identification number){validations.cin.required ? '' : ' (optional)'}
                </Form.Label>
                <Form.Text
                  name="cin"
                  ref={register({
                    required: validations.cin.required ? 'This field cannot be left blank' : false,
                    validate: { regex: cinRegex },
                  })}
                  readOnly={!!validations.cin.readOnly || !editable}
                  placeholder='Enter CIN Number'
                  id="cin"
                  onChange={(e) => handleCINOnChange(e.target.value)}
                />
                <ErrorWrapper errors={errors} fieldName='cin' />
              </Form.Group>
            </Col>
        )}
        <Col md={6}>
          <Form.Group>
            <Form.Label id="signingAuthorityName">
              Signing Authority Name
            </Form.Label>
            {
              validations.name.readOnly || validations.name.textField ? (
                <Form.Text
                  name="signingAuthorityName"
                  ref={register({
                    required: 'This field cannot be left blank',
                  })}
                  readOnly={!!validations.name.readOnly || !editable}
                  placeholder='Enter Signing Authority Name'
                  id="signingAuthorityName"
                />
              ) : (
                <Controller
                  as={CreatableSelect}
                  control={control}
                  options={
                    getAuthorityNames
                    ? getAuthorityNames.map(a => ({ label: a.full_name, value: a.full_name }))
                    : []
                  }
                  onCreateOption={validateAndSetName}
                  name="signingAuthorityName"
                  rules={{ required: 'This field cannot be left blank' }}
                  isDisabled={!!validations.name.readOnly || !editable}
                  placeholder='Enter or Select Signing Authority Name'
                  id="signingAuthorityName"
                />
              )
            }
            <ErrorWrapper errors={errors} fieldName='signingAuthorityName' />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label id="signingPanNumber">
              Signing Authority's PAN card number
            </Form.Label>
            <Form.Text
              name="signingPanNumber"
              ref={register({
                required: 'This field cannot be left blank',
                validate: { regex: pancardNumberRegex },
              })}
              readOnly={!!validations.pan.readOnly || !editable}
              placeholder='Enter Signatory’s PAN card number'
              id="signingPanNumber"
            />
            <ErrorWrapper errors={errors} fieldName='signingPanNumber' />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label id="signatoryEmail">
              Signing Authority Email ID
            </Form.Label>
            <Form.Text
              name="signatoryEmail"
              ref={register({
                required: 'This field cannot be left blank',
                validate: { emailRegex },
              })}
              placeholder='Enter Signatory Email ID'
              id="signatoryEmail"
              readOnly={!editable}
            />
            <ErrorWrapper errors={errors} fieldName='signatoryEmail' />
          </Form.Group>
        </Col>
      </Row>
      {
        editable && (
          <FormFooter
            tooltipProps={why}
            btnProps={{
              label: 'Proceed to KYC',
              type: "submit",
            }}
          />
        )
      }
    </form>
  )
};

const mapStateToProps = (state) => ({
  businessEntities: masterDataSelectors.businessEntities(state),
  merchantDetails: merchantSelectors.getMerchantDetails(state),
  filledDetails: merchantSelectors.getSigningAuthorityDetails(state),
  getAuthorityNames: signingAuthoritySelectors.getAuthorityNames(state),
});

const mapDispatchToProps = (dispatch) => ({
  fetchAuthorityNames: (cin) => dispatch(signingAuthorityActions.fetchAuthorityNames(cin)),
  submitSigningAuthorityDetails: formData =>
    dispatch(signingAuthorityActions.submitSigningAuthorityDetails(formData)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SigningAuthority);
